import { useMemo } from 'react'

// ─── Simple word-level diff ─────────────────────────────────────────────────

interface DiffSegment {
  text: string
  type: 'same' | 'removed' | 'added'
}

function computeWordDiff(oldText: string, newText: string): DiffSegment[] {
  const oldWords = oldText.split(/(\s+)/)
  const newWords = newText.split(/(\s+)/)
  const segments: DiffSegment[] = []

  // Simple LCS-based word diff
  const lcs = buildLCS(oldWords, newWords)
  let oi = 0
  let ni = 0

  for (const [lcsOi, lcsNi] of lcs) {
    // Words removed before this match
    while (oi < lcsOi) {
      segments.push({ text: oldWords[oi], type: 'removed' })
      oi++
    }
    // Words added before this match
    while (ni < lcsNi) {
      segments.push({ text: newWords[ni], type: 'added' })
      ni++
    }
    // Matching word
    segments.push({ text: oldWords[oi], type: 'same' })
    oi++
    ni++
  }

  // Remaining removed
  while (oi < oldWords.length) {
    segments.push({ text: oldWords[oi], type: 'removed' })
    oi++
  }
  // Remaining added
  while (ni < newWords.length) {
    segments.push({ text: newWords[ni], type: 'added' })
    ni++
  }

  return segments
}

function buildLCS(a: string[], b: string[]): [number, number][] {
  const m = a.length
  const n = b.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1])
    }
  }

  const result: [number, number][] = []
  let i = m
  let j = n
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      result.unshift([i - 1, j - 1])
      i--
      j--
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--
    } else {
      j--
    }
  }
  return result
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface ContextDiffViewProps {
  oldText?: string
  newText?: string
}

export function ContextDiffView({ oldText, newText }: ContextDiffViewProps) {
  const segments = useMemo(() => {
    if (!oldText && newText) {
      return [{ text: newText, type: 'added' as const }]
    }
    if (oldText && !newText) {
      return [{ text: oldText, type: 'removed' as const }]
    }
    if (!oldText || !newText) return []
    // Guard against O(m*n) explosion on very large texts — fall back to simple add/remove
    const oldWords = oldText.split(/(\s+)/).length
    const newWords = newText.split(/(\s+)/).length
    if (oldWords * newWords > 100_000) {
      return [
        { text: oldText, type: 'removed' as const },
        { text: newText, type: 'added' as const },
      ]
    }
    return computeWordDiff(oldText, newText)
  }, [oldText, newText])

  if (segments.length === 0) return null

  return (
    <div className="rounded-md border border-border bg-bg-deepest p-3 font-mono text-sm leading-relaxed">
      {segments.map((seg, i) => {
        if (seg.type === 'removed') {
          return (
            <span
              key={i}
              className="bg-accent-amber/15 text-accent-amber/70 line-through decoration-accent-amber/50"
            >
              {seg.text}
            </span>
          )
        }
        if (seg.type === 'added') {
          return (
            <span key={i} className="bg-accent-amber/20 text-accent-amber">
              {seg.text}
            </span>
          )
        }
        return <span key={i} className="text-text-secondary">{seg.text}</span>
      })}
    </div>
  )
}
